import React, { useEffect, useState } from 'react';
import {
  Control,
  Select,
  DatePicker,
  Clone,
  Delete,
  Warning,
} from 'components';
import {
  TimeWindowFormProps,
  TimeWindowHeaderWrapper,
} from 'components/TimeWindow';

import { Api } from 'api';
import { Controller, useFormContext } from 'react-hook-form';
import { TreeSelect } from 'components/TreeSelect';
import { CircleButton } from 'components/CircleButton';
import { useFormErrors } from 'hooks/useFormErrors';

export const CompositeReturnTimeWindow: React.FC<TimeWindowFormProps> = ({
  index,
  onClone,
  onRemove,
  refreshDates,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useFormContext();
  const [propertyValueOptions, setPropertyValueOptions] = useState([]);
  const { data: assetOptions } = Api.lookup.GetAssetSelectionSets();
  const { data: tagRefOptions } = Api.lookup.GetTagRefPropertyDefinitions();
  const fieldName = `timeWindows[${index}]`;
  const watchProperty = watch(`${fieldName}.tagRefPropertyDefinitionId`);
  const timeWindows = getValues('timeWindows');
  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });

  useEffect(() => {
    setPropertyValueOptions(
      tagRefOptions?.find((tag) => tag.value === watchProperty)?.tags ?? []
    );
  }, [watchProperty, tagRefOptions]);

  useEffect(() => {
    tagRefOptions?.forEach((tag) => {
      if (tag.value === watchProperty) {
        setPropertyValueOptions(tag.tags);
      }
    });
  }, [watchProperty, tagRefOptions]);

  return (
    <TimeWindowHeaderWrapper key={index}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignContent: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <Control label='From Date'>
          <Controller
            control={control}
            name={`${fieldName}.fromDate`}
            render={({ field: { onChange, value } }) => {
              return (
                <DatePicker
                  onChange={onChange}
                  onCalendarClose={() => refreshDates?.()}
                  selected={value}
                  testid={`${fieldName}.fromDate`}
                  errorMessage={getErrorMessage(`${fieldName}.fromDate`)}
                  style={{ width: 130 }}
                />
              );
            }}
          />
        </Control>
        <Control label='To Date'>
          <Controller
            control={control}
            name={`${fieldName}.toDate`}
            render={({ field: { onChange, value } }) => {
              return (
                <DatePicker
                  disabled
                  testid={`${fieldName}.toDate`}
                  onChange={onChange}
                  selected={value}
                  hasError={getErrorMessage(`${fieldName}.toDate`)}
                  style={{ width: 130 }}
                />
              );
            }}
          />
        </Control>
        <Control label='Selection Set'>
          <Controller
            control={control}
            name={`${fieldName}.assetSelectionSetId`}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  placeholder='Search for component'
                  testid={`${fieldName}.assetSelectionSetId.component-search`}
                  options={assetOptions || []}
                  onChange={(val) => {
                    onChange(val.value);
                  }}
                  value={[...(assetOptions || [])].filter(
                    (option: any) => option.value === value
                  )}
                  hasError={getErrorMessage(`${fieldName}.assetSelectionSetId`)}
                  style={{ width: 250 }}
                />
              );
            }}
          />
        </Control>
        <Control label='Property'>
          <Controller
            control={control}
            name={`${fieldName}.tagRefPropertyDefinitionId`}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  placeholder='Search for component'
                  testid={`${fieldName}.tagRefPropertyDefinitionId.component-search`}
                  options={tagRefOptions || []}
                  onChange={(val) => {
                    setValue(`${fieldName}.tagId`, null);
                    onChange(val.value);
                  }}
                  value={[...(tagRefOptions || [])].filter(
                    (option: any) => option.value === value
                  )}
                  hasError={getErrorMessage(
                    `${fieldName}.tagRefPropertyDefinitionId`
                  )}
                  style={{ width: 250 }}
                />
              );
            }}
          />
        </Control>
        <Control label='Property Value'>
          <Controller
            control={control}
            name={`${fieldName}.tagId`}
            render={({ field: { onChange, value } }) => {
              return (
                <TreeSelect
                  onChange={onChange}
                  testid={`${fieldName}.tagId.component-search`}
                  value={value}
                  options={propertyValueOptions}
                  isMulti={false}
                  hasError={getErrorMessage(`${fieldName}.tagId`)}
                />
              );
            }}
          />
        </Control>
      </div>
      <div
        style={{ display: 'flex', flexDirection: 'row', alignSelf: 'flex-end' }}
      >
        {hasErrors && <Warning size='13px' className='m-1' fill='#AA0101' />}
        <CircleButton
          key='clone'
          data-testid={`${fieldName}-clone`}
          icon={<Clone size='7px' />}
          title='Clone'
          onClick={() => {
            onClone?.({ ...getValues(fieldName) });
          }}
        />
        <CircleButton
          disabled={timeWindows.length === 1}
          key='delete'
          data-testid={`${fieldName}-delete`}
          icon={<Delete size='7px' />}
          title='Delete'
          onClick={() => {
            onRemove?.(index);
          }}
        />
      </div>
    </TimeWindowHeaderWrapper>
  );
};
